import React, { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import { Tabs, Tab, Box, TextField, IconButton, Collapse, CircularProgress } from '@material-ui/core'
import { Settings as SettingsIcon, ExpandMore, ExpandLess } from '@material-ui/icons'
import { ThemeProvider } from '@material-ui/core/styles'
import desertFloraTheme from '../../../styles/desertFloraTheme'
import DisposalFacilitiesTable from './DisposalFacilitiesTable'
import RolloffRatesTable from './RolloffRatesTable'
import DisposalQuickCalculator from './DisposalQuickCalculator'

const TabPanel = ({ children, value, index }) => {
  if (value !== index) return null
  return <Box>{children}</Box>
}

const FIELDS = [
  { key: 'dumpTruckCapacityTons', label: 'Truck Capacity', unit: 'tons', step: 1 },
  { key: 'dumpTruckCostPerMile', label: 'Truck $/Mile', unit: '$/mi', step: 0.25 },
  { key: 'dumpTrailerCapacityTons', label: 'Trailer Capacity', unit: 'tons', step: 1 },
  { key: 'dumpTrailerCostPerMile', label: 'Trailer $/Mile', unit: '$/mi', step: 0.25 },
  { key: 'operatorCostPerHour', label: 'Operator $/Hour', unit: '$/hr', step: 5 },
  { key: 'maxDisposalSearchMiles', label: 'Max Search Radius', unit: 'miles', step: 5 }
]

const HaulAssumptionsPanel = ({ url }) => {
  const [open, setOpen] = useState(false)
  const [values, setValues] = useState(null)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    axios.get(`${url}/api/bid-estimation/haul-assumptions`)
      .then(res => setValues(res.data))
      .catch(err => console.error('Failed to load haul assumptions:', err))
  }, [url])

  const handleChange = (key, val) => {
    setValues(prev => ({ ...prev, [key]: val }))
    setDirty(true)
  }

  const handleSave = useCallback(() => {
    if (!dirty || !values) return
    setSaving(true)
    axios.put(`${url}/api/bid-estimation/haul-assumptions`, values)
      .then(res => { setValues(res.data); setDirty(false) })
      .catch(err => console.error('Failed to save haul assumptions:', err))
      .finally(() => setSaving(false))
  }, [url, values, dirty])

  if (!values) return null

  return (
    <div style={{ margin: '0 8px 8px', background: 'var(--surface, #fbf8f1)', borderRadius: 10, border: '1px solid var(--line, #e0d8c2)', overflow: 'hidden' }}>
      <div
        onClick={() => setOpen(!open)}
        style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', cursor: 'pointer', gap: 8 }}>
        <SettingsIcon style={{ fontSize: 16, color: 'var(--ink-4, #97a094)' }} />
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3, #6f7a6b)', fontFamily: 'var(--font-mono)', flex: 1 }}>
          Hauling Cost Assumptions
        </span>
        {!open && (
          <span style={{ fontSize: 11, color: 'var(--ink-4, #97a094)' }}>
            Truck {values.dumpTruckCapacityTons}T @ ${values.dumpTruckCostPerMile}/mi · Trailer {values.dumpTrailerCapacityTons}T @ ${values.dumpTrailerCostPerMile}/mi · Operator ${values.operatorCostPerHour}/hr
          </span>
        )}
        {open ? <ExpandLess style={{ fontSize: 18, color: 'var(--ink-4)' }} /> : <ExpandMore style={{ fontSize: 18, color: 'var(--ink-4)' }} />}
      </div>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <div style={{ padding: '4px 12px 12px', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {FIELDS.map(f => (
            <div key={f.key} style={{ minWidth: 110 }}>
              <TextField
                size="small" variant="outlined"
                label={f.label}
                type="number"
                value={values[f.key] ?? ''}
                onChange={e => handleChange(f.key, e.target.value)}
                onBlur={handleSave}
                inputProps={{ min: 0, step: f.step }}
                InputProps={{ style: { fontSize: 13 }, endAdornment: <span style={{ fontSize: 10, color: 'var(--ink-4)', whiteSpace: 'nowrap' }}>{f.unit}</span> }}
                style={{ width: 130 }}
              />
            </div>
          ))}
          {saving && <CircularProgress size={16} />}
          {dirty && !saving && <span style={{ fontSize: 10, color: '#8B6914' }}>Unsaved</span>}
        </div>
      </Collapse>
    </div>
  )
}

const DisposalDashboard = ({ url }) => {
  const [tab, setTab] = React.useState(0)

  return (
    <ThemeProvider theme={desertFloraTheme}>
      <div style={{ background: 'var(--bg, #f4efe4)', minHeight: '100%', padding: '0 0 24px' }}>
        <Tabs
          value={tab}
          onChange={(e, newVal) => setTab(newVal)}
          indicatorColor="primary"
          textColor="primary"
          style={{ marginBottom: 8, borderBottom: '1px solid var(--line, #e0d8c2)' }}
        >
          <Tab label="Disposal Facilities" />
          <Tab label="Roll-off Rates" />
          <Tab label="Quick Calculator" />
        </Tabs>
        <HaulAssumptionsPanel url={url} />
        <TabPanel value={tab} index={0}>
          <DisposalFacilitiesTable url={url} />
        </TabPanel>
        <TabPanel value={tab} index={1}>
          <RolloffRatesTable url={url} />
        </TabPanel>
        <TabPanel value={tab} index={2}>
          <DisposalQuickCalculator url={url} />
        </TabPanel>
      </div>
    </ThemeProvider>
  )
}

export default DisposalDashboard
